import {
  formatCurrency,
  formatDate,
  NoWrap,
  numberFormat,
  transformDailyData,
  transformMonthlyData,
  transformWeeklyData,
  transformYearlyData,
} from "../../utils/helpers";
import { TransactionStatusTag } from "../../components/common/registerUtils";
import { InputType } from "../../types/common";
import {
  PARTNER_DAILY_COMMISSIONS_REPORT_API,
  PARTNER_MONTHLY_COMMISSIONS_REPORT_API,
} from "../../utils/apis";

type ReportType = "daily" | "weekly" | "monthly" | "yearly";
type TotalType = "total_amount" | "total_count" | undefined;

const getTableApiForReportType = (reportType: ReportType): string =>
  reportType === "daily" || reportType === "weekly"
    ? PARTNER_DAILY_COMMISSIONS_REPORT_API
    : PARTNER_MONTHLY_COMMISSIONS_REPORT_API;

const getGraphApiForReportType = (reportType: ReportType): string =>
  getTableApiForReportType(reportType) + "graphic-data/";

export const usePartnerCommissionSummary = () => {
  return {
    reportTitle: "Partner Commission Summary",

    customApi: (reportType: ReportType) => getTableApiForReportType(reportType),

    customGraphApi: (reportType: ReportType) =>
      getGraphApiForReportType(reportType),

    tableData: (results: any[], reportType: ReportType) => {
      if (reportType === "weekly") {
        const grouped: Record<string, any> = {};

        results.forEach((item: any) => {
          const week = Math.ceil(item.day_of_month / 7);
          const key = `${week}-${item.status}`;

          if (!grouped[key]) {
            grouped[key] = {
              total_amount: 0,
              total_count: 0,
              status: item.status,
            };
          }

          grouped[key].total_amount += Number(item.total_amount || 0);
          grouped[key].total_count += Number(item.total_count || 0);
        });

        return Object.entries(grouped)
          .sort(([a], [b]) => {
            const [weekA, statusA] = a.split("-");
            const [weekB, statusB] = b.split("-");
            return (
              Number(weekA) - Number(weekB) || statusA.localeCompare(statusB)
            );
          })
          .map(([key, item]: [string, any]) => {
            const [week] = key.split("-");
            return {
              week_of_month: `Week ${week}`,
              total_amount: formatCurrency(item.total_amount),
              total_count: numberFormat(item.total_count),
              status: <TransactionStatusTag status={item.status} />,
            };
          });
      }

      if (reportType === "yearly") {
        const grouped: Record<string, any> = {};

        results.forEach((item: any) => {
          const key = `${item.year_value}-${item.status}`;

          if (!grouped[key]) {
            grouped[key] = {
              total_amount: 0,
              total_count: 0,
              status: item.status,
              year: item.year_value,
            };
          }

          grouped[key].total_amount += Number(item.total_amount || 0);
          grouped[key].total_count += Number(item.total_count || 0);
        });

        return Object.values(grouped)
          .sort(
            (a: any, b: any) =>
              Number(a.year) - Number(b.year) ||
              a.status.localeCompare(b.status),
          )
          .map((item: any) => ({
            year_value: item.year,
            total_amount: formatCurrency(item.total_amount),
            total_count: numberFormat(item.total_count),
            status: <TransactionStatusTag status={item.status} />,
          }));
      }

      // daily / monthly
      return results.map((item: any) => ({
        ...item,
        date: (
          <NoWrap>
            {reportType === "daily"
              ? formatDate(item.date)
              : item.month_name || item.date}
          </NoWrap>
        ),
        total_amount: formatCurrency(item.total_amount || "0"),
        total_count: numberFormat(item.total_count || "0"),
        status: <TransactionStatusTag status={item.status} />,
      }));
    },

    tableColumns: (reportType: ReportType) => [
      ...(reportType === "daily" ? [{ id: "date", header: "Date" }] : []),
      ...(reportType === "monthly"
        ? [{ id: "month_name", header: "Month" }]
        : []),
      ...(reportType === "weekly"
        ? [{ id: "week_of_month", header: "Week" }]
        : []),
      ...(reportType === "yearly"
        ? [{ id: "year_value", header: "Year" }]
        : []),
      { id: "total_amount", header: "Total Amount", align: "right" },
      { id: "total_count", header: "Total Count" },
      {
        id: "status",
        header: "Status",
        filter: {
          type: "select",
          options: [
            { id: "successful", name: "Success" },
            { id: "failed", name: "Failed" },
            { id: "pending", name: "Pending" },
            { id: "posted", name: "Posted" },
          ],
        },
      },
    ],

    graphData: (
      results: any[],
      reportType: ReportType,
      totalType: TotalType,
    ) => {
      if (!results.length) return { xLabels: [], series: [], yLabel: "" };

      let transformed;
      if (reportType === "daily")
        transformed = transformDailyData(results, totalType);
      else if (reportType === "weekly")
        transformed = transformWeeklyData(results, totalType);
      else if (reportType === "yearly")
        transformed = transformYearlyData(results, totalType);
      else transformed = transformMonthlyData(results, totalType);

      return {
        ...transformed,
        yLabel: totalType === "total_amount" ? "Total Amount" : "Total Count",
      };
    },

    externalFilters: [
      {
        id: "start_date",
        header: "Date Range",
        type: "date" as InputType,
        rangeNames: {
          startKey: "start_date",
          endKey: "end_date",
        },
      },
    ],

    initialFilters: {
      status: "successful",
    },

    exportData: {
      /**
       * Returns the correct export endpoint for the active report type,
       * mirroring the same daily / monthly split used for data fetching.
       */
      getEndpoint: (reportType: ReportType) =>
        reportType === "daily" || reportType === "weekly"
          ? `${PARTNER_DAILY_COMMISSIONS_REPORT_API}export-to-csv`
          : `${PARTNER_MONTHLY_COMMISSIONS_REPORT_API}export-to-csv`,
      fileName: "partner-commissions-summary-report.xlsx",
      buttonText: "Export CSV",
    },

    totalTypeOptions: [
      { label: "Total Amount", value: "total_amount" },
      { label: "Total Count", value: "total_count" },
    ],

    showReportTypeFilter: true,
    showTotalTypeFilter: true,
    showMonthFilter: true,
    showYearFilter: true,
    defaultReportType: "monthly" as ReportType,
    defaultTotalType: "total_amount" as TotalType,
  };
};
